import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../services/supabase'
import { useCurrentAdvisor } from './useCurrentAdvisor'

export function useInboxCounts() {
  const { advisor } = useCurrentAdvisor()
  const [count, setCount] = useState(0)

  const refresh = useCallback(async (advisorId) => {
    // Unread notification count (mentions + assigned + watching)
    const { count: notifCount } = await supabase
      .from('sfp_notifications')
      .select('id', { count: 'exact', head: true })
      .eq('advisor_id', advisorId)
      .eq('is_read', false)
      .in('type', ['mention', 'task_reassigned', 'task_watcher_added'])

    // Unread DM count: messages not sent by me, not deleted, not read by me
    const { data: convs } = await supabase
      .from('sfp_dm_conversations')
      .select('id')
      .or(`advisor_a_id.eq.${advisorId},advisor_b_id.eq.${advisorId}`)

    let dmUnread = 0
    if ((convs ?? []).length > 0) {
      const convIds = convs.map(c => c.id)
      const { data: msgs } = await supabase
        .from('sfp_dm_messages')
        .select('id')
        .in('conversation_id', convIds)
        .neq('sender_id', advisorId)
        .is('deleted_at', null)

      const msgIds = (msgs ?? []).map(m => m.id)
      if (msgIds.length > 0) {
        const { count: readCount } = await supabase
          .from('sfp_dm_reads')
          .select('message_id', { count: 'exact', head: true })
          .in('message_id', msgIds)
          .eq('advisor_id', advisorId)
        dmUnread = msgIds.length - (readCount ?? 0)
      }
    }

    setCount((notifCount ?? 0) + dmUnread)
  }, [])

  useEffect(() => {
    if (!advisor?.id) return
    refresh(advisor.id)

    const ch = supabase
      .channel(`inbox-counts-${advisor.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'sfp_notifications', filter: `advisor_id=eq.${advisor.id}` }, () => refresh(advisor.id))
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'sfp_dm_messages' }, () => refresh(advisor.id))
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'sfp_dm_reads' }, () => refresh(advisor.id))
      .subscribe()

    return () => supabase.removeChannel(ch)
  }, [advisor?.id, refresh])

  return count
}
